import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";

import { setAudioEnabled } from "../app/actions/audio/audioSlice";
import { MediaPreviewHandle } from "../types/media-preview";
import { DEFAULT_FPS } from "../utils/timecode";

/**
 * メッセージ本文中の `[MM:SS]` クリックで、メッセージ履歴を遡って
 * 直近の video/audio 添付をシーク再生するためのフック。
 *
 * - TalkRoom / DM のどちらからも利用できるよう、メッセージ型は
 *   `id` と `files[].{id, file_type}` だけに制約した最小形にしている。
 * - `messages` は ref 経由で参照するため、`handleTimestampClick` の
 *   関数参照は安定（依存配列が空）で、`MessageItem` の再レンダリングを抑えやすい。
 */
export interface SeekableMessage {
  id: string;
  files?: ReadonlyArray<{
    id: string;
    file_type: string | null;
  }>;
}

export interface UseTimestampSeekResult {
  /** MessageItem の onMediaRef に渡す */
  handleMediaRef: (
    messageId: string,
    fileId: string,
    handle: MediaPreviewHandle | null,
  ) => void;
  /** MessageItem の onPlayStateChange に渡す */
  handlePlayStateChange: (playing: boolean) => void;
  /**
   * MessageItem の onTimestampClick から `clickedMessageId` を添えて呼び出す。
   * （MessageItem 側の型は `(seconds, mediaItemId, frames?)` なので、ラップして渡す）
   * frames はフレーム番号（[M:SS.FF] の FF / [N] の絶対フレーム）。添付メディアの
   * fps は取得できないため DEFAULT_FPS で秒に換算する。
   */
  handleTimestampClick: (
    seconds: number,
    mediaItemId: string | null,
    clickedMessageId?: string,
    frames?: number | null,
  ) => void;
}

// スクロール完了待ち（scrollIntoView）後にシークするためのディレイ
const SCROLL_BEFORE_SEEK_DELAY_MS = 400;

const isPlayableMedia = (fileType: string | null | undefined): boolean =>
  fileType === "video" || fileType === "audio";

export function useTimestampSeek<T extends SeekableMessage>(
  messages: ReadonlyArray<T>,
): UseTimestampSeekResult {
  const dispatch = useDispatch();

  // `${messageId}:${fileId}` をキーに MediaPreview の参照を保持
  const mediaRefsMap = useRef<Record<string, MediaPreviewHandle>>({});

  // 最新のメッセージ配列を ref に保持（ハンドラの依存配列を空に保つ）
  const messagesRef = useRef<ReadonlyArray<T>>(messages);
  messagesRef.current = messages;

  const handleMediaRef = useCallback<UseTimestampSeekResult["handleMediaRef"]>(
    (messageId, fileId, handle) => {
      const key = `${messageId}:${fileId}`;
      if (handle) {
        mediaRefsMap.current[key] = handle;
      } else {
        delete mediaRefsMap.current[key];
      }
    },
    [],
  );

  const handlePlayStateChange = useCallback<
    UseTimestampSeekResult["handlePlayStateChange"]
  >(
    (playing) => {
      if (playing) dispatch(setAudioEnabled(true));
    },
    [dispatch],
  );

  const handleTimestampClick = useCallback<
    UseTimestampSeekResult["handleTimestampClick"]
  >((seconds, _mediaItemId, clickedMessageId, frames) => {
    const targetSeconds = frames != null ? seconds + frames / DEFAULT_FPS : seconds;
    const list = messagesRef.current;
    if (list.length === 0) return;

    const msgIndex = clickedMessageId
      ? list.findIndex((m) => m.id === clickedMessageId)
      : list.length - 1;
    const searchFrom = msgIndex >= 0 ? msgIndex : list.length - 1;

    for (let i = searchFrom; i >= 0; i--) {
      const msg = list[i];
      const avFile = msg.files?.find((f) => isPlayableMedia(f.file_type));
      if (!avFile) continue;

      const handle = mediaRefsMap.current[`${msg.id}:${avFile.id}`];
      const el = document.querySelector(`[data-message-id="${msg.id}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });

      if (handle) {
        const doSeek = () => {
          const current = handle.getCurrentTime();
          handle.seek(targetSeconds - current);
          handle.play().catch(() => {});
        };
        if (el) {
          setTimeout(doSeek, SCROLL_BEFORE_SEEK_DELAY_MS);
        } else {
          doSeek();
        }
      }
      return;
    }
  }, []);

  return { handleMediaRef, handlePlayStateChange, handleTimestampClick };
}
